import {
  Button,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import { useState, useEffect } from 'react';
import AutoDeleteIcon from '@mui/icons-material/AutoDelete';
import { useTranslation } from 'react-i18next';
import { movementService, productService, warehouseService } from 'services/api';
import ReactFinalForm from 'components/form/ReactFinalForm';
import { IdentityField } from 'components/form/IdentityField';
import { IOption } from 'interfaces';
import { TextFieldAdapter } from 'components/form/TextFieldAdapter';
import { AutocompleteAdapter } from 'components/form/AutocompleteAdapter';
import { DatePickerAdapter } from 'components/form/DatePickerAdapter';
import toast from 'features/toast';

interface DeleteFilteredMovementsButtonProps {
  refresh?: any;
}

export function DeleteFilteredMovementsButton({ refresh }: DeleteFilteredMovementsButtonProps) {
  const { t } = useTranslation();

  const [openDialog, setOpenDialog] = useState(false);

  const [productOptions, setProductOptions] = useState<IOption[]>([]);
  const [warehouseOptions, setWarehouseOptions] = useState<IOption[]>([]);

  useEffect(() => {
    // Fetch product and warehouse options here
    productService.getAllBaseInformation().then(setProductOptions);
    warehouseService.getAllBaseInformation().then(setWarehouseOptions);
  }, []);

  const toggleOpenDialog = () => {
    setOpenDialog(!openDialog);
  };

  const onSubmit = (values: any) => {
    movementService.deleteByFilter(values).then((res) => {
      if (res.status === 200) {
        if (refresh) refresh?.current();
        toast.success(t('actions.successfully.multi-deleted'));
        setOpenDialog(false);
      } else {
        if (res.data?.warining) {
          toast.warning(res.data.warining);
        }
        if (res.data?.errors) {
          toast.error(res.data.errors);
        }
      }
    });
  };

  return (
    <>
      <Button
        variant="outlined"
        size="small"
        onClick={toggleOpenDialog}
        color="warning"
        title={t('actions.delete-filtered')}>
        <AutoDeleteIcon />
      </Button>
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="md">
        <DialogTitle id="alert-dialog-title">{t(`global.delete-confirmation`)}</DialogTitle>
        <DialogContent>
          <ReactFinalForm onSubmit={onSubmit} formId="deleteFilteredMovements" hideControls>
            <div className="flex flex-row flex-wrap">
              <div className="flex-1 p-2">
                <IdentityField
                  name="product"
                  component={AutocompleteAdapter}
                  options={productOptions}
                  label={t('global.product')}
                />
              </div>
              <div className="flex-1 p-2">
                <IdentityField
                  name="warehouse"
                  component={AutocompleteAdapter}
                  options={warehouseOptions}
                  label={t('global.warehouse')}
                />
              </div>
              <div className="flex-1 p-2">
                <IdentityField
                  name="product_batch"
                  component={TextFieldAdapter}
                  label={t('entities.movement.product_batch')}
                />
              </div>
              <div className="flex-1 p-2">
                <IdentityField
                  name="date_start"
                  component={DatePickerAdapter}
                  label={t('global.date_start')}
                  type="date"
                />
              </div>
              <div className="flex-1 p-2">
                <IdentityField
                  name="date_end"
                  component={DatePickerAdapter}
                  label={t('global.date_end')}
                  type="date"
                />
              </div>
            </div>
          </ReactFinalForm>
        </DialogContent>
        <DialogActions>
          <div className="w-full flex justify-between">
            <Button onClick={toggleOpenDialog}>{t('actions.cancel')}</Button>
            <Button
              type="submit"
              form="deleteFilteredMovements"
              color="error"
              variant="contained"
              autoFocus>
              {t('actions.delete')}
            </Button>
          </div>
        </DialogActions>
      </Dialog>
    </>
  );
}
